import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import AccessChangeRequestDialog from './AccessChangeRequestDialog';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  Business,
  Email,
  Shield,
  AccountCircle,
  Assignment,
  LocationOn
} from '@mui/icons-material';

const UserProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [pendingChangeRequest, setPendingChangeRequest] = useState(null);
  const [changeRequestLoading, setChangeRequestLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || user.full_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const fetchMyChangeRequest = useCallback(async () => {
    if (!user || !['viewer', 'inspector'].includes(user.role)) return;
    try {
      const response = await authService.getMyAccessRequest();
      const request = response.request;
      setPendingChangeRequest(request?.request_type === 'access_change' && request.status === 'pending' ? request : null);
    } catch (error) {
      console.error('Failed to load access change request:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchMyChangeRequest();
  }, [fetchMyChangeRequest]);

  const cancelChangeRequest = async () => {
    setChangeRequestLoading(true);
    try {
      await authService.cancelAccessChangeRequest();
      setPendingChangeRequest(null);
      setSuccessMessage('Access change request cancelled.');
    } catch (error) {
      setErrors({ general: error.error || 'Failed to cancel access change request' });
    } finally {
      setChangeRequestLoading(false);
    }
  };

  // Fetch user permissions
  useEffect(() => {
    const fetchUserPermissions = async () => {
      const userPermissionScope = user?.permissionScope || user?.permission_scope;
      if (user) {
        // For organization-level users, show organization access
        if (userPermissionScope === 'organization') {
          setPermissionsLoading(true);
          try {
            const orgPermissions = [{
              id: 'org_access',
              type: 'Organization',
              name: `Full ${user.organizationName || 'Organization'} Access`,
              permissionType: 'write',
              category: 'Location'
            }];
            
            // Fetch discipline permissions from backend
            const response = await authService.getUserPermissions(user.id);
            if (response.success && response.permissions) {
              // Add discipline permissions if available
              if (response.permissions.disciplines && response.permissions.disciplines.length > 0) {
                const disciplinePermissions = response.permissions.disciplines.map(discipline => ({
                  id: discipline.id,
                  type: 'Discipline',
                  name: discipline.name,
                  permissionType: 'access',
                  category: 'Discipline'
                }));
                orgPermissions.push(...disciplinePermissions);
              }
              
              if (response.permissions.sub_disciplines && response.permissions.sub_disciplines.length > 0) {
                const subDisciplinePermissions = response.permissions.sub_disciplines.map(subDiscipline => ({
                  id: `sub_${subDiscipline}`,
                  type: 'Sub-Discipline',
                  name: subDiscipline,
                  permissionType: 'access',
                  category: 'Discipline'
                }));
                orgPermissions.push(...subDisciplinePermissions);
              }
            }
            
            setUserPermissions(orgPermissions);
          } catch (error) {
            console.error('Error fetching organization user permissions:', error);
            // Fallback to just showing organization access
            setUserPermissions([{
              id: 'org_access',
              type: 'Organization',
              name: `Full ${user.organizationName || 'Organization'} Access`,
              permissionType: 'write',
              category: 'Location'
            }]);
          } finally {
            setPermissionsLoading(false);
          }
          return;
        }
        
        // For network/section-level users, fetch specific permissions
        if (userPermissionScope === 'network_section') {
          setPermissionsLoading(true);
          try {
            const response = await authService.getUserPermissions(user.id);
            if (response.success && response.permissions) {
              const allPermissions = [];
              
              // Transform location permissions
              const locationPermissions = (response.permissions.locations || []).map(permission => {
                if (permission.network_id && !permission.section_id) {
                  return {
                    id: permission.network_id,
                    type: 'Network',
                    name: permission.network_name || `Network ${permission.network_id}`,
                    permissionType: permission.permission_type || 'write',
                    category: 'Location'
                  };
                } else if (permission.section_id) {
                  return {
                    id: permission.section_id,
                    type: 'Section',
                    name: permission.section_name || `Section ${permission.section_id}`,
                    permissionType: permission.permission_type || 'write',
                    category: 'Location',
                    networkName: permission.section_network_name
                  };
                }
                return null;
              }).filter(Boolean);

              // Transform discipline permissions
              const disciplinePermissions = (response.permissions.disciplines || []).map(discipline => ({
                id: discipline.id,
                type: 'Discipline',
                name: discipline.name,
                permissionType: 'access',
                category: 'Discipline'
              }));

              // Transform sub-discipline permissions
              const subDisciplinePermissions = (response.permissions.sub_disciplines || []).map(subDiscipline => ({
                id: `sub_${subDiscipline}`,
                type: 'Sub-Discipline',
                name: subDiscipline,
                permissionType: 'access',
                category: 'Discipline'
              }));

              allPermissions.push(...locationPermissions, ...disciplinePermissions, ...subDisciplinePermissions);
              
              setUserPermissions(allPermissions);
            }
          } catch (error) {
            console.error('Error fetching user permissions:', error);
          } finally {
            setPermissionsLoading(false);
          }
        }
      }
    };

    fetchUserPermissions();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await authService.updateProfile(formData);
      
      if (response.success) {
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
        
        // Refresh user data
        await refreshUser();
      } else {
        setErrors({
          general: response.error || 'Failed to update profile. Please try again.'
        });
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      setErrors({
        general: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    if (user) {
      setFormData({
        fullName: user.fullName || user.full_name || '',
        email: user.email || ''
      });
    }
    setErrors({});
    setSuccessMessage('');
    setIsEditing(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'system_admin': return 'error';
      case 'manager': return 'primary';
      case 'inspector': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'system_admin': return 'System Admin';
      case 'manager': return 'Manager';
      case 'inspector': return 'Inspector';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  };

  const getPermissionScopeLabel = (scope) => {
    switch (scope) {
      case 'organization': return 'Organization-wide Access';
      case 'network_section': return 'Network/Section Level Access';
      default: return scope;
    }
  };

  // Helper function to get initials from full name
  const getInitials = (fullName) => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return names[0].charAt(0) + names[names.length - 1].charAt(0);
    }
    return names[0].charAt(0);
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading user profile...
          </Typography>
        </Stack>
      </Container>
    );
  }

  const userFullName = user.fullName || user.full_name || '';
  const permissionScope = user.permissionScope || user.permission_scope;
  const organizationName = user.organizationName || user.organization_name || 'N/A';
  const createdAt = user.createdAt || user.created_at;
  const lastLogin = user.lastLogin || user.last_login;
  const emailVerified = user.emailVerified ?? user.email_verified ?? false;
  const formatDate = (value, includeTime = false) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not available';
    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  };

  const detailRow = (label, value) => (
    <Box sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.4 }}>
        {label}
      </Typography>
      {typeof value === 'string' ? <Typography variant="body2" fontWeight={500}>{value}</Typography> : value}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AccountCircle sx={{ fontSize: 30, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5">User Profile</Typography>
            <Typography variant="body2" color="text.secondary">Manage your personal information and account access</Typography>
          </Box>
        </Stack>
        {!isEditing && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {['viewer', 'inspector'].includes(user.role) && !pendingChangeRequest && (
            <Button variant="outlined" startIcon={<Assignment />} onClick={() => setChangeDialogOpen(true)}>Request Access Change</Button>
          )}
          <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Edit Profile</Button>
        </Stack>}
      </Stack>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2.5}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.75rem', fontWeight: 600 }}>
            {getInitials(userFullName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', sm: '2rem' }, mb: 0.5 }}>{userFullName || 'User'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, overflowWrap: 'anywhere' }}>{user.email}</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {user.role && <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" />}
              {permissionScope && <Chip label={getPermissionScopeLabel(permissionScope)} variant="outlined" size="small" />}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}
      {errors.general && <Alert severity="error" sx={{ mb: 3 }}>{errors.general}</Alert>}
      {pendingChangeRequest && (
        <Alert severity="warning" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" disabled={changeRequestLoading} onClick={cancelChangeRequest}>Cancel Request</Button>
        }>
          Your access change request is awaiting manager review. Your current permissions remain active.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 3 }}>
                <Person color="primary" />
                <Typography variant="h6">Personal Information</Typography>
              </Stack>
              {isEditing ? (
                <Stack spacing={2.5}>
                  <TextField fullWidth label="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} disabled={isLoading} error={!!errors.fullName} helperText={errors.fullName} />
                  <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleInputChange} disabled={isLoading} error={!!errors.email} helperText={errors.email} InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} /> }} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                    <Button variant="contained" startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Save />} onClick={handleSave} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancel} disabled={isLoading}>Cancel</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2.5}>
                  <Box><Typography variant="caption" color="text.secondary">Full Name</Typography><Typography variant="body1" fontWeight={500}>{userFullName || 'Not provided'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Email Address</Typography><Typography variant="body1" fontWeight={500} sx={{ overflowWrap: 'anywhere' }}>{user.email || 'Not provided'}</Typography></Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                <Business color="primary" />
                <Typography variant="h6">Account Details</Typography>
              </Stack>
              {detailRow('Organization', organizationName)}
              {detailRow('Role', <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" />)}
              {permissionScope && detailRow('Access Level', getPermissionScopeLabel(permissionScope))}
              {createdAt && detailRow('Member Since', formatDate(createdAt))}
              {detailRow('Email Status', <Chip label={emailVerified ? 'Verified' : 'Unverified'} color={emailVerified ? 'success' : 'warning'} size="small" />)}
              {lastLogin && detailRow('Last Sign In', formatDate(lastLogin, true))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Shield color="primary" />
              <Box>
                <Typography variant="h6">Access Permissions</Typography>
                <Typography variant="body2" color="text.secondary">Networks, sections, and disciplines available to your account</Typography>
              </Box>
            </Stack>
            {!permissionsLoading && <Chip label={`${userPermissions.length} assigned`} size="small" variant="outlined" />}
          </Stack>

          {permissionsLoading ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 3 }}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Loading permissions...</Typography></Stack>
          ) : userPermissions.length > 0 ? (
            <Stack spacing={3}>
              {['Location', 'Discipline'].map(category => {
                const categoryPermissions = userPermissions.filter(permission => permission.category === category);
                if (!categoryPermissions.length) return null;
                return (
                  <Box key={category}>
                    <Typography variant="subtitle2" sx={{ mb: 1.25 }}>{category} Access</Typography>
                    <Grid container spacing={1.5}>
                      {categoryPermissions.map(permission => (
                        <Grid item xs={12} sm={6} md={4} key={`${category}-${permission.type}-${permission.id}`}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, height: '100%', bgcolor: 'background.default' }}>
                            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                              {category === 'Location' ? <LocationOn color={permission.type === 'Network' ? 'primary' : 'secondary'} sx={{ mt: 0.25 }} /> : <Assignment color="primary" sx={{ mt: 0.25 }} />}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{permission.name}</Typography>
                                {permission.networkName && <Typography variant="caption" color="text.secondary">{permission.networkName} Network</Typography>}
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                  <Chip label={permission.type} size="small" variant="outlined" color={permission.type === 'Network' ? 'primary' : permission.type === 'Section' ? 'secondary' : 'info'} />
                                  {category === 'Location' && <Chip label={permission.permissionType === 'write' ? 'Read/Write' : 'Read Only'} size="small" variant="outlined" color={permission.permissionType === 'write' ? 'success' : 'info'} />}
                                </Stack>
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Alert severity="info">No specific permissions assigned. Contact your administrator for access.</Alert>
          )}
        </CardContent>
      </Card>
      <AccessChangeRequestDialog
        open={changeDialogOpen}
        onClose={() => setChangeDialogOpen(false)}
        user={user}
        onSubmitted={() => {
          setSuccessMessage('Access change request submitted to your manager.');
          fetchMyChangeRequest();
        }}
      />
    </Container>
  );
};

export default UserProfilePage;
